import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { Card } from '@shared/ui/containers/Card';
import { PillTabs } from '@shared/ui/lists/PillTabs';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { Section } from '@shared/ui/containers/Section';
import { cn } from '@shared/lib/cn';
import { pageCopy } from '@shared/lib/pageCopy';
import { fetchFaqCategories } from './api';

function FaqAccordionItem({
  question,
  answer,
  open,
  onToggle,
  index,
}: {
  question: string;
  answer: string;
  open: boolean;
  onToggle: () => void;
  index: number;
}): JSX.Element {
  return (
    <div
      className={cn(
        'overflow-hidden transition-colors',
        open && 'bg-primary-50/40',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'w-full flex items-start gap-3 px-4 py-4 text-left transition-colors',
          open ? 'bg-transparent' : 'hover:bg-sunken/40',
        )}
        aria-expanded={open}
      >
        <span
          className={cn(
            'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-caption font-bold tabular',
            open ? 'bg-primary-700 text-white' : 'bg-sunken text-ink-secondary',
          )}
          aria-hidden
        >
          {index + 1}
        </span>
        <span className="min-w-0 flex-1 text-body font-semibold leading-snug text-ink">{question}</span>
        <ChevronDown
          className={cn(
            'mt-1 h-5 w-5 shrink-0 text-ink-secondary transition-transform duration-200',
            open && 'rotate-180 text-primary-700',
          )}
          strokeWidth={2}
          aria-hidden
        />
      </button>
      {open ? (
        <div className="hairline-t mx-4 px-0 py-4 pl-[3.25rem]">
          <p className="text-body leading-relaxed text-ink-secondary whitespace-pre-wrap">{answer}</p>
        </div>
      ) : null}
    </div>
  );
}

/** Static FAQ reference — categories with expandable Q&amp;A entries. */
export function Faq(): JSX.Element {
  const copy = pageCopy().faq;
  const query = useQuery({ queryKey: ['faq'], queryFn: fetchFaqCategories });
  const categories = query.data ?? [];
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);

  const activeCategory = useMemo(
    () => categories.find((c) => c.faq_category_id === activeCategoryId) ?? categories[0],
    [categories, activeCategoryId],
  );

  useEffect(() => {
    if (!activeCategory?.entries.length) return;
    setOpenEntryId((prev) => {
      if (prev && activeCategory.entries.some((e) => e.faq_entry_id === prev)) return prev;
      return activeCategory.entries[0]?.faq_entry_id ?? null;
    });
  }, [activeCategory]);

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />

      <div className="flex flex-col gap-4 px-5 pt-4 pb-safe-or-6">
        {query.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-28 w-full rounded-lg" />
            <Skeleton className="h-28 w-full rounded-lg" />
          </div>
        ) : null}

        {query.isError ? (
          <ErrorState
            title="Couldn't load FAQ."
            description="Please try again."
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : null}

        {!query.isLoading && !query.isError && categories.length === 0 ? (
          <EmptyState
            icon={<HelpCircle className="h-7 w-7" strokeWidth={1.5} />}
            title="No FAQ entries yet"
            description="Your estate has not published any questions and answers."
          />
        ) : null}

        {categories.length > 0 && activeCategory ? (
          <>
            {categories.length > 1 ? (
              <PillTabs
                ariaLabel="FAQ categories"
                options={categories.map((cat) => ({
                  value: cat.faq_category_id,
                  label: cat.name,
                }))}
                value={activeCategory.faq_category_id}
                onChange={(nextId) => {
                  setActiveCategoryId(nextId);
                  setOpenEntryId(null);
                }}
              />
            ) : null}

            <Card noGrain className="px-4 py-3">
              <p className="text-label uppercase tracking-wide text-ink-muted">Category</p>
              <h2 className="mt-1 text-headline text-ink">{activeCategory.name}</h2>
              {activeCategory.description ? (
                <p className="mt-1.5 text-body-sm leading-relaxed text-ink-secondary">
                  {activeCategory.description}
                </p>
              ) : null}
              <p className="mt-2 text-caption text-ink-muted">
                {activeCategory.entries.length}{' '}
                {activeCategory.entries.length === 1 ? 'question' : 'questions'}
              </p>
            </Card>

            <Section
              title="Questions"
              description="Tap a question to expand the answer."
              className="mt-0"
            >
              <ListGroup>
                {activeCategory.entries.map((entry, index) => (
                  <FaqAccordionItem
                    key={entry.faq_entry_id}
                    index={index}
                    question={entry.question}
                    answer={entry.answer}
                    open={openEntryId === entry.faq_entry_id}
                    onToggle={() =>
                      setOpenEntryId((prev) => (prev === entry.faq_entry_id ? null : entry.faq_entry_id))
                    }
                  />
                ))}
              </ListGroup>
            </Section>
          </>
        ) : null}
      </div>
    </div>
  );
}
